import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { handleApiError } from '../api/client';
import * as billingApi from '../api/billing';
import * as filesApi from '../api/files';
import * as transcriptionsApi from '../api/transcriptions';
import { useFiles } from '../hooks/useFiles';
import { estimateMediaDurationSeconds, prepareAudioUpload } from '../utils/audioExtraction';
import { calculateFileHash } from '../utils/file-hash';
import { assertUploadAllowedByQuota } from '../utils/uploadQuotaPrecheck';
import { cleanupStaleMultipartResumeSessions } from '../utils/multipartResumeCleanup';
import posthog from '../lib/posthog';

export type UploadJobStatus =
  | 'queued'
  | 'preparing'
  | 'hashing'
  | 'uploading'
  | 'creating_transcription'
  | 'finalizing'
  | 'complete'
  | 'error'
  | 'canceled';

export interface UploadTranscriptionOptions {
  language: string; // 'auto' or ISO language code
  speakerDetection: boolean;
}

export interface UploadJob {
  id: string;
  createdAtMs: number;
  file: File;
  displayName: string;
  status: UploadJobStatus;
  stageLabel: string;
  progress: number; // 0..100
  error: string | null;
  transcriptionId: string | null;
  options: UploadTranscriptionOptions;
}

interface UploadManagerContextType {
  jobs: UploadJob[];
  trayHidden: boolean;
  trayOpen: boolean;
  setTrayOpen: (open: boolean) => void; // user intent (manual open/close)
  setTrayHovering: (hovering: boolean) => void;
  noteTrayInteraction: () => void;
  closeTray: () => void;
  enqueueUploads: (files: File[], options: UploadTranscriptionOptions) => void;
  cancelJob: (jobId: string) => void;
  retryJob: (jobId: string) => void;
  dismissJob: (jobId: string) => void;
}

const UploadManagerContext = createContext<UploadManagerContextType | undefined>(undefined);

function createJobId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `job_${Math.random().toString(16).slice(2)}_${Date.now()}`;
}

function clampProgress(progress: number) {
  if (!Number.isFinite(progress)) return 0;
  return Math.max(0, Math.min(100, progress));
}

class UploadCanceledError extends Error {
  constructor() {
    super('Upload canceled');
    this.name = 'UploadCanceledError';
  }
}

function isAbortError(err: unknown) {
  if (!err || typeof err !== 'object') return false;
  const maybe = err as { name?: unknown };
  return maybe.name === 'AbortError';
}

export function UploadManagerProvider({
  children,
  maxConcurrentUploads = 3,
}: {
  children: React.ReactNode;
  maxConcurrentUploads?: number;
}) {
  const { uploadFile } = useFiles();
  const [jobs, setJobs] = useState<UploadJob[]>([]);
  const [trayHidden, setTrayHidden] = useState(false);
  const [trayOpenState, setTrayOpenState] = useState(false);
  const [trayHovering, setTrayHovering] = useState(false);
  const [lastTrayInteractionMs, setLastTrayInteractionMs] = useState(0);
  const [suppressAutoOpenUntilIdle, setSuppressAutoOpenUntilIdle] = useState(false);
  const [schedulerTick, setSchedulerTick] = useState(0);

  const runningJobsRef = useRef<Set<string>>(new Set());
  const canceledJobsRef = useRef<Set<string>>(new Set());
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const mountedRef = useRef(true);
  const jobsRef = useRef<UploadJob[]>([]);
  const trayOpenRef = useRef(false);
  const trayHoveringRef = useRef(false);
  const lastInteractionRef = useRef(0);
  const successCollapseTimerRef = useRef<number | null>(null);
  const successDismissTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      for (const ctrl of abortControllersRef.current.values()) {
        try {
          ctrl.abort();
        } catch {
          // ignore
        }
      }
    };
  }, []);

  useEffect(() => {
    void cleanupStaleMultipartResumeSessions({
      abortMultipartUpload: filesApi.abortMultipartUpload,
      confirmUpload: filesApi.confirmUpload,
    });
  }, []);

  useEffect(() => {
    jobsRef.current = jobs;
    trayOpenRef.current = trayOpenState;
    trayHoveringRef.current = trayHovering;
    lastInteractionRef.current = lastTrayInteractionMs;
  }, [jobs, lastTrayInteractionMs, trayHovering, trayOpenState]);

  const noteTrayInteraction = useCallback(() => {
    setLastTrayInteractionMs(Date.now());
  }, []);

  const setTrayOpen = useCallback(
    (open: boolean) => {
      noteTrayInteraction();
      if (open) setTrayHidden(false);
      setTrayOpenState(open);
      const activeJobs = jobs.some((j) =>
        ['queued', 'preparing', 'hashing', 'uploading', 'creating_transcription', 'finalizing'].includes(j.status)
      );
      if (!open && activeJobs) {
        setSuppressAutoOpenUntilIdle(true);
      }
      if (open) {
        setSuppressAutoOpenUntilIdle(false);
      }
    },
    [jobs, noteTrayInteraction]
  );

  const openTraySystem = useCallback(() => {
    setSuppressAutoOpenUntilIdle(false);
    setTrayHidden(false);
    setTrayOpenState(true);
  }, []);

  const closeTray = useCallback(() => {
    noteTrayInteraction();
    setTrayOpenState(false);
    setTrayHidden(true);
    const activeJobs = jobsRef.current.some((j) =>
      ['queued', 'preparing', 'hashing', 'uploading', 'creating_transcription', 'finalizing'].includes(j.status)
    );
    if (activeJobs) {
      setSuppressAutoOpenUntilIdle(true);
    }
  }, [noteTrayInteraction]);

  const updateJob = useCallback((jobId: string, patch: Partial<UploadJob>) => {
    setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, ...patch } : j)));
  }, []);

  const isJobCanceled = useCallback((jobId: string) => canceledJobsRef.current.has(jobId), []);

  const runJob = useCallback(
    async (job: UploadJob) => {
      const jobId = job.id;
      if (isJobCanceled(jobId)) throw new UploadCanceledError();

      updateJob(jobId, { status: 'preparing', stageLabel: 'Converting to audio', progress: 0, error: null });

      // Best-effort quota precheck to avoid expensive work + uploading when transcription will be rejected.
      let estimatedDurationSeconds: number | undefined;
      try {
        estimatedDurationSeconds = await estimateMediaDurationSeconds(job.file);
      } catch {
        estimatedDurationSeconds = undefined;
      }

      let quota: Awaited<ReturnType<typeof billingApi.getQuota>> = null;
      try {
        quota = await billingApi.getQuota();
      } catch {
        quota = null;
      }
      if (quota) {
        assertUploadAllowedByQuota(quota, estimatedDurationSeconds);
      }

      const prepared = await prepareAudioUpload(job.file, (progressInfo) => {
        if (isJobCanceled(jobId)) return;
        const stageWeight = 10;
        const normalized = Math.max(0, Math.min(1, progressInfo.progress));
        const p = progressInfo.stage === 'loading'
          ? Math.min(stageWeight * 0.5, normalized * stageWeight * 0.5)
          : stageWeight * 0.5 + normalized * stageWeight * 0.5;
        updateJob(jobId, { progress: clampProgress(p) });
      });
      if (isJobCanceled(jobId)) throw new UploadCanceledError();
      updateJob(jobId, { progress: 10 });

      updateJob(jobId, { status: 'hashing', stageLabel: 'Calculating file hash' });
      const { hashHex } = await calculateFileHash(prepared.file, (hashProgress) => {
        if (isJobCanceled(jobId)) return;
        updateJob(jobId, { progress: clampProgress(10 + hashProgress * 10) });
      });
      if (isJobCanceled(jobId)) throw new UploadCanceledError();
      updateJob(jobId, { progress: 20 });

      const abortController = new AbortController();
      abortControllersRef.current.set(jobId, abortController);

      updateJob(jobId, { status: 'uploading', stageLabel: 'Uploading to cloud storage' });
      const uploadedFile = await uploadFile(
        prepared.file,
        (uploadProgress) => {
          if (isJobCanceled(jobId)) return;
          updateJob(jobId, { progress: clampProgress(20 + uploadProgress * 0.4) });
        },
        prepared.estimatedDurationSeconds,
        job.file.name,
        hashHex ?? undefined,
        () => {
          if (isJobCanceled(jobId)) return;
          updateJob(jobId, { stageLabel: 'Instant upload', progress: 60 });
        },
        abortController.signal,
        true
      );
      abortControllersRef.current.delete(jobId);
      if (isJobCanceled(jobId)) throw new UploadCanceledError();
      updateJob(jobId, { progress: 60 });

      updateJob(jobId, { status: 'creating_transcription', stageLabel: 'Creating transcription job', progress: 70 });
      const transcription = await transcriptionsApi.createTranscription({
        file_id: uploadedFile.id,
        language: job.options.language === 'auto' ? undefined : job.options.language,
        options: {
          speaker_diarization: job.options.speakerDetection,
        },
      });
      if (isJobCanceled(jobId)) throw new UploadCanceledError();
      updateJob(jobId, { transcriptionId: transcription.id, progress: 90 });

      updateJob(jobId, { status: 'finalizing', stageLabel: 'Queuing for transcription', progress: 95 });
      await new Promise((r) => setTimeout(r, 300));
      if (isJobCanceled(jobId)) throw new UploadCanceledError();

      updateJob(jobId, { status: 'complete', stageLabel: 'Complete', progress: 100 });
      posthog.capture('file_upload_completed', {
        file_name: job.file.name,
        file_size_bytes: job.file.size,
        language: job.options.language,
        speaker_detection: job.options.speakerDetection,
        transcription_id: transcription.id,
      });
      window.dispatchEvent(new Event('transcriptions:changed'));
    },
    [isJobCanceled, updateJob, uploadFile]
  );

  const startJob = useCallback(
    (jobId: string) => {
      if (runningJobsRef.current.has(jobId)) return;
      const job = jobs.find((j) => j.id === jobId);
      if (!job) return;
      if (job.status !== 'queued') return;

      runningJobsRef.current.add(jobId);
      void runJob(job)
        .catch((err) => {
          abortControllersRef.current.delete(jobId);
          if (err instanceof UploadCanceledError || isAbortError(err)) {
            updateJob(jobId, { status: 'canceled', stageLabel: 'Canceled', error: null });
            return;
          }
          const errorMessage = handleApiError(err);
          updateJob(jobId, { status: 'error', stageLabel: 'Failed', error: errorMessage });
          posthog.capture('file_upload_failed', {
            error_message: errorMessage,
          });
        })
        .finally(() => {
          runningJobsRef.current.delete(jobId);
          if (mountedRef.current) {
            setSchedulerTick((prev) => prev + 1);
          }
        });
    },
    [jobs, runJob, updateJob]
  );

  useEffect(() => {
    const max = Math.max(1, maxConcurrentUploads);
    const available = max - runningJobsRef.current.size;
    if (available <= 0) return;

    const queued = jobs.filter((j) => j.status === 'queued').slice(0, available);
    for (const job of queued) startJob(job.id);
  }, [jobs, maxConcurrentUploads, schedulerTick, startJob]);

  const enqueueUploads = useCallback((files: File[], options: UploadTranscriptionOptions) => {
    const now = Date.now();
    const newJobs: UploadJob[] = files.map((file) => ({
      id: createJobId(),
      createdAtMs: now,
      file,
      displayName: file.name,
      status: 'queued',
      stageLabel: 'Queued',
      progress: 0,
      error: null,
      transcriptionId: null,
      options,
    }));
    setJobs((prev) => [...newJobs, ...prev]);
    posthog.capture('file_upload_started', {
      file_count: files.length,
      language: options.language,
      speaker_detection: options.speakerDetection,
    });
    noteTrayInteraction();
    openTraySystem();
  }, [noteTrayInteraction, openTraySystem]);

  useEffect(() => {
    const activeJobs = jobs.some((j) =>
      ['queued', 'preparing', 'hashing', 'uploading', 'creating_transcription', 'finalizing'].includes(j.status)
    );
    if (!activeJobs && suppressAutoOpenUntilIdle) {
      setSuppressAutoOpenUntilIdle(false);
    }
  }, [jobs, suppressAutoOpenUntilIdle]);

  useEffect(() => {
    const hasAttentionNeeded = jobs.some((j) => j.status === 'error' || j.status === 'canceled');
    if (hasAttentionNeeded) {
      openTraySystem();
      return;
    }
    if (!trayOpenState && !suppressAutoOpenUntilIdle) {
      // no-op; we don't auto-open on active jobs (only on enqueue) to avoid repeated popups.
    }
  }, [jobs, openTraySystem, suppressAutoOpenUntilIdle, trayOpenState]);

  useEffect(() => {
    const clearTimers = () => {
      if (successCollapseTimerRef.current !== null) {
        window.clearTimeout(successCollapseTimerRef.current);
        successCollapseTimerRef.current = null;
      }
      if (successDismissTimerRef.current !== null) {
        window.clearTimeout(successDismissTimerRef.current);
        successDismissTimerRef.current = null;
      }
    };

    const allComplete = jobs.length > 0 && jobs.every((j) => j.status === 'complete');
    const hasAttentionNeeded = jobs.some((j) => j.status === 'error' || j.status === 'canceled');
    if (!allComplete || hasAttentionNeeded) {
      clearTimers();
      return;
    }

    // Auto-collapse disabled - these constants are no longer used
    // const SUCCESS_COLLAPSE_MIN_MS = 4000;
    // const SUCCESS_COLLAPSE_NO_INTERACTION_MS = 10000;
    const SUCCESS_DISMISS_AFTER_COLLAPSE_MS = 15000;

    if (trayHovering) {
      clearTimers();
      return;
    }

    // Disabled auto-collapse: tray stays open until user manually closes it
    // if (trayOpenState) {
    //   const now = Date.now();
    //   const sinceInteraction = now - lastTrayInteractionMs;
    //   const delayMs = Math.max(SUCCESS_COLLAPSE_MIN_MS, SUCCESS_COLLAPSE_NO_INTERACTION_MS - sinceInteraction);
    //   successCollapseTimerRef.current = window.setTimeout(() => {
    //     const currentJobs = jobsRef.current;
    //     const stillAllComplete =
    //       currentJobs.length > 0 && currentJobs.every((j) => j.status === 'complete');
    //     const stillNoAttentionNeeded = !currentJobs.some((j) => j.status === 'error' || j.status === 'canceled');
    //     if (!stillAllComplete || !stillNoAttentionNeeded) return;
    //     if (trayHoveringRef.current) return;
    //     const since = Date.now() - lastInteractionRef.current;
    //     if (since < SUCCESS_COLLAPSE_NO_INTERACTION_MS) return;
    //     setTrayOpenState(false);
    //   }, delayMs);
    //   return () => {
    //     if (successCollapseTimerRef.current !== null) {
    //       window.clearTimeout(successCollapseTimerRef.current);
    //       successCollapseTimerRef.current = null;
    //     }
    //   };
    // }

    // Tray is collapsed; auto-dismiss completed jobs after a grace period so the tray disappears.
    successDismissTimerRef.current = window.setTimeout(() => {
      const currentJobs = jobsRef.current;
      const stillAllComplete =
        currentJobs.length > 0 && currentJobs.every((j) => j.status === 'complete');
      const stillNoAttentionNeeded = !currentJobs.some((j) => j.status === 'error' || j.status === 'canceled');
      if (!stillAllComplete || !stillNoAttentionNeeded) return;
      if (trayOpenRef.current) return;
      if (trayHoveringRef.current) return;
      setJobs([]);
    }, SUCCESS_DISMISS_AFTER_COLLAPSE_MS);

    return () => {
      if (successDismissTimerRef.current !== null) {
        window.clearTimeout(successDismissTimerRef.current);
        successDismissTimerRef.current = null;
      }
    };
  }, [jobs, lastTrayInteractionMs, trayHovering, trayOpenState]);

  const cancelJob = useCallback(
    (jobId: string) => {
      canceledJobsRef.current.add(jobId);
      abortControllersRef.current.get(jobId)?.abort();
      updateJob(jobId, { status: 'canceled', stageLabel: 'Canceled', error: null });
    },
    [updateJob]
  );

  const retryJob = useCallback(
    (jobId: string) => {
      canceledJobsRef.current.delete(jobId);
      abortControllersRef.current.delete(jobId);
      updateJob(jobId, {
        status: 'queued',
        stageLabel: 'Queued',
        progress: 0,
        error: null,
        transcriptionId: null,
      });
      setTrayOpen(true);
    },
    [updateJob]
  );

  const dismissJob = useCallback((jobId: string) => {
    canceledJobsRef.current.delete(jobId);
    abortControllersRef.current.delete(jobId);
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
  }, []);

  const value: UploadManagerContextType = useMemo(
    () => ({
      jobs,
      trayHidden,
      trayOpen: trayOpenState,
      setTrayOpen,
      setTrayHovering,
      noteTrayInteraction,
      closeTray,
      enqueueUploads,
      cancelJob,
      retryJob,
      dismissJob,
    }),
    [
      cancelJob,
      closeTray,
      dismissJob,
      enqueueUploads,
      jobs,
      noteTrayInteraction,
      retryJob,
      setTrayOpen,
      trayHidden,
      trayOpenState,
    ]
  );

  return <UploadManagerContext.Provider value={value}>{children}</UploadManagerContext.Provider>;
}

export function useUploadManager(): UploadManagerContextType {
  const ctx = useContext(UploadManagerContext);
  if (!ctx) throw new Error('useUploadManager must be used within an UploadManagerProvider');
  return ctx;
}
